// src/lib/validation.ts
// Input validation schemas for security

interface ValidationResult {
  isValid: boolean;
  error: string | null;
}

interface ValidationResultWithSanitized extends ValidationResult {
  sanitized: string;
}

interface ValidationResultWithStrength extends ValidationResult {
  strength: 'none' | 'weak' | 'medium' | 'strong';
}

interface FileUploadOptions {
  maxSizeMB?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
}

/**
 * Sanitizes a string by removing potentially dangerous characters
 */
export const sanitizeString = (input: unknown): string => {
  if (typeof input !== 'string') return '';

  return input
    .trim()
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/data:/gi, '') // Remove data: protocol
    .substring(0, 5000); // Limit length
};

/**
 * Validates email format
 */
export const validateEmail = (email: unknown): ValidationResult => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const sanitized = sanitizeString(email);

  if (sanitized !== email) {
    return { isValid: false, error: 'Email contains invalid characters' };
  }

  if (!emailRegex.test(sanitized)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  if (sanitized.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }

  return { isValid: true, error: null };
};

/**
 * Validates password strength
 */
export const validatePassword = (password: unknown): ValidationResultWithStrength => {
  if (!password || typeof password !== 'string') {
    return { isValid: false, error: 'Password is required', strength: 'none' };
  }

  if (password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters', strength: 'weak' };
  }

  if (password.length > 128) {
    return { isValid: false, error: 'Password is too long', strength: 'weak' };
  }

  // Check for common patterns that should be avoided
  const commonPatterns = [
    /(.)\1{3,}/, // Repeated characters
    /^password/i,
    /^123456/,
    /^qwerty/i,
    /^admin/i,
  ];

  for (const pattern of commonPatterns) {
    if (pattern.test(password)) {
      return { isValid: false, error: 'Password is too common or predictable', strength: 'weak' };
    }
  }

  // Calculate strength
  let strength: 'weak' | 'medium' | 'strong' = 'weak';
  let score = 0;

  if (password.length >= 12) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score >= 4) strength = 'strong';
  else if (score >= 3) strength = 'medium';

  return { isValid: true, error: null, strength };
};

/**
 * Validates journal entry content
 */
export const validateJournalContent = (content: unknown): ValidationResultWithSanitized => {
  if (!content || typeof content !== 'string') {
    return { isValid: false, error: 'Content is required', sanitized: '' };
  }

  if (content.trim().length === 0) {
    return { isValid: false, error: 'Content cannot be empty', sanitized: '' };
  }

  if (content.length > 50000) {
    return { isValid: false, error: 'Content is too long (max 50,000 characters)', sanitized: '' };
  }

  // Basic sanitization while preserving formatting
  const sanitized = content
    .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
    .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '') // Remove iframe tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .trim();

  return { isValid: true, error: null, sanitized };
};

/**
 * Validates community post content
 */
export const validatePostContent = (content: unknown): ValidationResultWithSanitized => {
  if (!content || typeof content !== 'string') {
    return { isValid: false, error: 'Content is required', sanitized: '' };
  }

  if (content.trim().length === 0) {
    return { isValid: false, error: 'Content cannot be empty', sanitized: '' };
  }

  if (content.length > 10000) {
    return { isValid: false, error: 'Post is too long (max 10,000 characters)', sanitized: '' };
  }

  // Basic sanitization
  const sanitized = content
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();

  return { isValid: true, error: null, sanitized };
};

/**
 * Validates display name
 */
export const validateDisplayName = (name: unknown): ValidationResultWithSanitized => {
  if (!name || typeof name !== 'string') {
    return { isValid: false, error: 'Display name is required', sanitized: '' };
  }

  const sanitized = sanitizeString(name);

  if (sanitized.length === 0) {
    return { isValid: false, error: 'Display name cannot be empty', sanitized: '' };
  }

  if (sanitized.length > 100) {
    return {
      isValid: false,
      error: 'Display name is too long (max 100 characters)',
      sanitized: '',
    };
  }

  // Check for inappropriate content patterns
  const inappropriatePatterns = [
    /admin/i,
    /moderator/i,
    /support/i,
    /official/i,
    /\b(fuck|shit|damn|hell|bitch|ass|cunt|dick|pussy|cock|tits|nigger|faggot|retard|gay|lesbian|homo|whore|slut)\b/i,
  ];

  for (const pattern of inappropriatePatterns) {
    if (pattern.test(sanitized)) {
      return {
        isValid: false,
        error: 'Display name contains inappropriate content',
        sanitized: '',
      };
    }
  }

  return { isValid: true, error: null, sanitized };
};

/**
 * Validates URL format
 */
export const validateUrl = (url: unknown): ValidationResult => {
  if (!url || typeof url !== 'string') {
    return { isValid: false, error: 'URL is required' };
  }

  try {
    const urlObj = new URL(url);

    // Only allow http/https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return { isValid: false, error: 'Only HTTP and HTTPS URLs are allowed' };
    }

    // Basic domain validation
    if (!urlObj.hostname || urlObj.hostname.length > 253) {
      return { isValid: false, error: 'Invalid domain name' };
    }

    return { isValid: true, error: null };
  } catch {
    return { isValid: false, error: 'Invalid URL format' };
  }
};

/**
 * Rate limiting helper for client-side protection
 */
export const checkRateLimit = (
  key: string,
  maxAttempts: number = 5,
  windowMs: number = 60000
): boolean => {
  const now = Date.now();
  const storageKey = `rateLimit_${key}`;

  try {
    const stored = localStorage.getItem(storageKey);
    const attempts: number[] = stored ? JSON.parse(stored) : [];

    // Remove expired attempts
    const validAttempts = attempts.filter(time => now - time < windowMs);

    if (validAttempts.length >= maxAttempts) {
      return false; // Rate limit exceeded
    }

    // Add current attempt
    validAttempts.push(now);
    localStorage.setItem(storageKey, JSON.stringify(validAttempts));

    return true; // Action allowed
  } catch (storageError) {
    console.warn('Rate limiting error:', storageError);
    return true; // Allow if storage fails
  }
};

/**
 * Validates file upload
 */
export const validateFileUpload = (
  file: File | null,
  options: FileUploadOptions = {}
): ValidationResult => {
  const {
    maxSizeMB = 10,
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  } = options;

  if (!file) {
    return { isValid: false, error: 'No file selected' };
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return { isValid: false, error: `File is too large. Maximum size is ${maxSizeMB}MB` };
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, error: 'File type not allowed' };
  }

  // Check file extension
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!allowedExtensions.includes(fileExtension)) {
    return { isValid: false, error: 'File extension not allowed' };
  }

  // Check for potentially dangerous filenames
  const dangerousPatterns = [
    /\.(exe|bat|cmd|scr|pif|com|vbs|js|jar|app|deb|rpm)$/i,
    /[<>:"|?*]/,
    /^\./,
    /\.\./,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(file.name)) {
      return { isValid: false, error: 'Invalid filename' };
    }
  }

  return { isValid: true, error: null };
};

// Export types for use in other modules
export type {
  ValidationResult,
  ValidationResultWithSanitized,
  ValidationResultWithStrength,
  FileUploadOptions,
};
